// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Search, Download, Filter, Eye, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

interface SpecialRequest {
  id: string;
  event_name: string;
  event_id: string;
  registrant_name: string;
  registrant_email: string;
  team_name: string;
  coach_name: string;
  coach_email: string;
  age_group_name: string;
  special_requests: string;
  submitted_at: string;
  registration_status: string;
  registration_id: string;
}

const EventSpecialRequestsView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState<SpecialRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<SpecialRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');
  const [events, setEvents] = useState<{id: string, name: string}[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<SpecialRequest | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Check for event filter from URL parameters
  useEffect(() => {
    const eventFilterParam = searchParams.get('eventFilter');
    if (eventFilterParam && eventFilterParam !== 'all') {
      setEventFilter(eventFilterParam);
      console.log('🔗 Applied event filter from URL:', eventFilterParam);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchSpecialRequests();
    fetchEvents();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [requests, searchTerm, statusFilter, eventFilter]);

  const fetchSpecialRequests = async () => {
    try {
      setLoading(true);
      
      // Get all registration responses with special requests, including the responses JSONB field and form_id
      const { data: responses, error: responsesError } = await supabase
        .from('event_registration_responses')
        .select('id, special_requests, age_group_name, submitted_at, registration_id, event_id, responses, form_id')
        .not('special_requests', 'is', null)
        .neq('special_requests', '')
        .order('submitted_at', { ascending: false });

      if (responsesError) throw responsesError;

      if (!responses || responses.length === 0) {
        setRequests([]);
        return;
      }

      console.log('🎯 Found responses with special requests:', responses.length);

      // Get unique form IDs to fetch form definitions
      const formIds = [...new Set(responses.map(r => r.form_id).filter(Boolean))];
      console.log('📋 Form IDs to fetch:', formIds);

      // Get form definitions to map field IDs to labels
      const { data: forms, error: formsError } = await supabase
        .from('event_registration_forms')
        .select('id, name, fields')
        .in('id', formIds);

      if (formsError) throw formsError;

      // Create form lookup map
      const formMap = new Map(forms.map(f => [f.id, f]));
      console.log('📄 Form definitions loaded:', forms.length);

      // Get event details
      const eventIds = [...new Set(responses.map(r => r.event_id).filter(Boolean))];
      
      const { data: events, error: eventsError } = await supabase
        .from('events')
        .select('id, name')
        .in('id', eventIds);

      if (eventsError) throw eventsError;

      // Create event lookup map
      const eventMap = new Map(events.map(e => [e.id, e]));

      // Helper function to extract team and coach info using form field definitions
      const extractInfoFromResponses = (responsesData: any, formId: string) => {
        console.log('🔍 EXTRACTING FROM RESPONSES:', responsesData, 'Form ID:', formId);
        
        let teamName = '';
        let coachName = '';
        let coachEmail = '';
        let registrantName = '';
        let registrantEmail = '';
        
        const form = formMap.get(formId);
        if (!form || !form.fields || !Array.isArray(form.fields)) {
          console.log('❌ No form definition found for form ID:', formId);
          return { teamName, coachName, coachEmail, registrantName, registrantEmail };
        }

        console.log('📋 Form fields:', form.fields);

        // Create field ID to field definition map
        const fieldMap = new Map();
        form.fields.forEach((field: any) => {
          fieldMap.set(field.id?.toString(), field);
        });

        console.log('🗺️ Field ID mapping:', Array.from(fieldMap.entries()));
        
        if (!responsesData || typeof responsesData !== 'object') {
          console.log('❌ Invalid responses data:', responsesData);
          return { teamName, coachName, coachEmail, registrantName, registrantEmail };
        }
        
        try {
          // Look through all key-value pairs in the responses object
          for (const [fieldId, value] of Object.entries(responsesData)) {
            if (value && typeof value === 'string') {
              const field = fieldMap.get(fieldId);
              const valueTrimmed = value.trim();
              
              if (field) {
                console.log(`🔎 Processing field ${fieldId} (${field.label}): "${valueTrimmed}"`);
                
                // Use the field definition to determine what this field represents
                if (field.is_coach_name_field === true || 
                    field.role_tag === 'coach_name' || 
                    field.label?.toLowerCase().includes('coach name')) {
                  console.log('✅ FOUND COACH NAME:', valueTrimmed);
                  coachName = valueTrimmed;
                }
                else if (field.is_coach_email_field === true || 
                         field.role_tag === 'coach_email' || 
                         field.label?.toLowerCase().includes('coach email')) {
                  console.log('✅ FOUND COACH EMAIL:', valueTrimmed);
                  coachEmail = valueTrimmed;
                }
                else if (field.role_tag === 'team_name' || 
                         field.label?.toLowerCase().includes('team name')) {
                  console.log('✅ FOUND TEAM NAME:', valueTrimmed);
                  teamName = valueTrimmed;
                }
                // Generic fallbacks based on field label
                else if (field.label?.toLowerCase().includes('name') && !coachName && !teamName) {
                  console.log('✅ FOUND REGISTRANT NAME:', valueTrimmed);
                  registrantName = valueTrimmed;
                }
                else if (field.label?.toLowerCase().includes('email') && valueTrimmed.includes('@') && !coachEmail) {
                  console.log('✅ FOUND REGISTRANT EMAIL:', valueTrimmed);
                  registrantEmail = valueTrimmed;
                }
              } else {
                console.log(`⚠️ No field definition found for field ID: ${fieldId}`);
              }
            }
          }
        } catch (error) {
          console.error('💥 Error extracting from responses:', error);
        }
        
        const result = { teamName, coachName, coachEmail, registrantName, registrantEmail };
        console.log('🎯 FINAL EXTRACTION RESULT:', result);
        return result;
      };

      // Process each response
      const formattedRequests: SpecialRequest[] = responses.map(response => {
        const event = eventMap.get(response.event_id);
        
        // Extract team and coach information from the responses JSONB field
        const { teamName, coachName, coachEmail, registrantName, registrantEmail } = extractInfoFromResponses(response.responses, response.form_id);

        return {
          id: response.id,
          event_name: event?.name || 'Unknown Event',
          event_id: response.event_id || '',
          registrant_name: registrantName || 'Unknown',
          registrant_email: registrantEmail || 'Unknown',
          team_name: teamName || 'Unknown',
          coach_name: coachName || 'Unknown',
          coach_email: coachEmail || 'Unknown',
          age_group_name: response.age_group_name || 'N/A',
          special_requests: response.special_requests || '',
          submitted_at: response.submitted_at,
          registration_status: 'unknown', // We don't have registration status without the registration record
          registration_id: response.registration_id || ''
        };
      }).filter(req => req.event_id); // Only include valid requests

      console.log('📊 Final requests processed:', formattedRequests.length);
      setRequests(formattedRequests);
    } catch (error) {
      console.error('Error fetching special requests:', error);
      toast.error('Failed to load special requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const filterRequests = () => {
    let filtered = requests;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(request =>
        request.event_name.toLowerCase().includes(term) ||
        request.registrant_name.toLowerCase().includes(term) ||
        request.registrant_email.toLowerCase().includes(term) ||
        request.team_name.toLowerCase().includes(term) ||
        request.coach_name.toLowerCase().includes(term) ||
        request.coach_email.toLowerCase().includes(term) ||
        request.special_requests.toLowerCase().includes(term) ||
        request.age_group_name.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.registration_status === statusFilter);
    }

    // Event filter
    if (eventFilter !== 'all') {
      filtered = filtered.filter(request => request.event_id === eventFilter);
    }

    setFilteredRequests(filtered);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Paid</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800"><AlertCircle className="h-3 w-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const exportToCSV = () => {
    const headers = ['Event', 'Registrant Name', 'Email', 'Team Name', 'Coach Name', 'Coach Email', 'Age Group', 'Special Request', 'Status', 'Date Submitted'];
    const rows = filteredRequests.map(request => [
      request.event_name,
      request.registrant_name,
      request.registrant_email,
      request.team_name,
      request.coach_name,
      request.coach_email,
      request.age_group_name,
      request.special_requests.replace(/"/g, '""'), // Escape quotes for CSV
      request.registration_status,
      format(new Date(request.submitted_at), 'yyyy-MM-dd HH:mm:ss')
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `event-special-requests-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const viewDetails = (request: SpecialRequest) => {
    setSelectedRequest(request);
    setShowDetails(true);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading Special Requests...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Event Special Requests Management
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            View and manage all special requests from event registrations across all events.
          </p>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search requests, names, events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select value={eventFilter} onValueChange={setEventFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Event" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {events.map(event => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button onClick={exportToCSV} variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold">{filteredRequests.length}</div>
                <div className="text-sm text-muted-foreground">Total Requests</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-2xl font-bold">
                  {new Set(filteredRequests.map(r => r.event_id)).size}
                </div>
                <div className="text-sm text-muted-foreground">Events with Requests</div>
              </CardContent>
            </Card>
          </div>

          {/* Requests Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Team Name</TableHead>
                  <TableHead>Coach Name</TableHead>
                  <TableHead>Age Group</TableHead>
                  <TableHead>Special Request</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No special requests found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell className="font-medium">
                        {request.event_name}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {request.team_name !== 'Unknown' ? request.team_name : '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {request.coach_name !== 'Unknown' ? request.coach_name : '-'}
                          </div>
                          {request.coach_email !== 'Unknown' && (
                            <div className="text-sm text-muted-foreground">{request.coach_email}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{request.age_group_name}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[300px]">
                        <div className="truncate" title={request.special_requests}>
                          {request.special_requests.length > 100 
                            ? `${request.special_requests.substring(0, 100)}...`
                            : request.special_requests
                          }
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewDetails(request)}
                          className="flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Details Modal */}
      {showDetails && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Special Request Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Event</label>
                  <div className="font-medium">{selectedRequest.event_name}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Age Group</label>
                  <div><Badge variant="outline">{selectedRequest.age_group_name}</Badge></div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Registrant Name</label>
                  <div className="font-medium">{selectedRequest.registrant_name}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Email</label>
                  <div>{selectedRequest.registrant_email}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Team Name</label>
                  <div className="font-medium">{selectedRequest.team_name}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Coach Name</label>
                  <div className="font-medium">{selectedRequest.coach_name}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Coach Email</label>
                  <div>{selectedRequest.coach_email}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Registration Status</label>
                  <div>{getStatusBadge(selectedRequest.registration_status)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Submitted</label>
                  <div>{format(new Date(selectedRequest.submitted_at), 'MMM dd, yyyy HH:mm')}</div>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-muted-foreground">Special Request</label>
                <Textarea
                  value={selectedRequest.special_requests}
                  readOnly
                  className="mt-1 min-h-[150px]"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDetails(false)}>
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default EventSpecialRequestsView;